// The write-ahead buffer (docs/superpowers/specs/2026-10-13-multi-ticket-drafts-design.md,
// "Saving, syncing, and failure"): every change is written to browser storage BEFORE the
// server save, and removed once the server has acknowledged a newer revision. What is left
// after a reload, a network failure or an expired session is replayed on the next load.
//
// One entry per user and draft, under `crm.draft-buffer.v1.<userId>.<draftId>`. The user id
// is in the key because the browser's storage belongs to the desk, not to a person.
//
//   { baseRevision, body }   `body` is the PUT body of the unsaved change and `baseRevision`
//                            the revision it was built on.
//
// Storage is injected (`window.localStorage`, or a fake in tests) and EVERY call is wrapped:
// localStorage throws in private windows and when the quota is full, and losing the buffer
// is not worth taking the screen down. A failed write reports `false`; it never throws.

const PREFIX = 'crm.draft-buffer.v1.';

/** window.localStorage, or null where reading it throws; every use of it is wrapped. */
export function browserStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function bufferKey(userId, draftId) {
  return `${PREFIX}${userId}.${draftId}`;
}

/** An entry as stored, or null when it is not one this module wrote. */
function parseEntry(raw) {
  if (!raw) return null;
  try {
    const entry = JSON.parse(raw);
    if (!entry || typeof entry !== 'object' || !Number.isInteger(entry.baseRevision) || !entry.body) return null;
    return { baseRevision: entry.baseRevision, body: entry.body };
  } catch {
    return null;
  }
}

export function createBuffer(storage) {
  function read(userId, draftId) {
    try {
      return parseEntry(storage.getItem(bufferKey(userId, draftId)));
    } catch {
      return null;
    }
  }

  function remove(userId, draftId) {
    try {
      storage.removeItem(bufferKey(userId, draftId));
    } catch {
      // Nothing useful to do; see the header.
    }
  }

  return {
    /** Replaces the entry. Returns whether it was stored. */
    write(userId, draftId, { baseRevision, body }) {
      try {
        storage.setItem(bufferKey(userId, draftId), JSON.stringify({ baseRevision, body }));
        return true;
      } catch {
        return false;
      }
    },

    read,

    /**
     * The server acknowledged `revision`. Removes the entry only if it was built on an
     * OLDER revision: an entry built on `revision` or later holds an edit that save did not
     * carry, and must survive.
     */
    ack(userId, draftId, revision) {
      const entry = read(userId, draftId);
      if (entry && entry.baseRevision < revision) remove(userId, draftId);
    },

    /** Drops the entry unconditionally (a closed draft, or a conflict the agent has seen). */
    remove,

    /** This user's unacknowledged entries, in storage order. */
    pending(userId) {
      const prefix = `${PREFIX}${userId}.`;
      const found = [];
      try {
        // Collect the keys first: reading while iterating by index is only safe if nothing
        // is removed in between, and a corrupt entry is skipped, not removed.
        const keys = [];
        for (let i = 0; i < storage.length; i += 1) {
          const key = storage.key(i);
          if (key && key.startsWith(prefix)) keys.push(key);
        }
        for (const key of keys) {
          const entry = parseEntry(storage.getItem(key));
          if (entry) found.push({ draftId: key.slice(prefix.length), ...entry });
        }
      } catch {
        return [];
      }
      return found;
    },
  };
}
